<template>
    <v-row v-if="user">
        <!-- TABS -->
        <v-col cols="12">
            <kimsa-tabs
                :tabs="tabs"
                @change="currentTab = $event"
                :value="currentTab"
            />
        </v-col>
        <!-- ACCOUNT DATA -->
        <template v-if="currentTab === 'personalInfo'">
            <v-form v-model="valid" ref="form" class="pb-3">
                <v-row class="px-3">
                    <v-col cols="12" md="6">
                        <kimsa-text-field
                            label="Nombre"
                            :rules="allRules.name"
                            @change="name = $event"
                            :value="name"
                            outlined dense
                            :disabled="!editMode"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kimsa-text-field
                            label="Apellido"
                            :rules="allRules.lastName"
                            @change="lastName = $event"
                            :value="lastName"
                            outlined dense
                            :disabled="!editMode"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kimsa-date-picker
                            label="Fecha de Nacimiento"
                            :rules="allRules.birthDate"
                            @change="birthDate = $event"
                            :value="birthDate"
                            outlined dense birth-date-mode
                            min="1900-01-01"
                            max="2020-12-31"
                            :disabled="!editMode"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kimsa-text-field
                            label="Rut"
                            :rules="allRules.rut"
                            @change="rut = $event"
                            :value="rut"
                            outlined dense
                            :disabled="!editMode"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kimsa-text-field
                            label="Teléfono"
                            :rules="allRules.phone"
                            @change="phone = $event"
                            :value="phone"
                            outlined dense
                            :prefix="phoneCode"
                            :disabled="!editMode"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <kimsa-text-field
                            v-if="!editMode"
                            label="Sexo"
                            :value="genderValue"
                            outlined dense
                            :disabled="!editMode"
                        />
                        <kimsa-select
                            v-else
                            label="Sexo"
                            :rules="allRules.gender"
                            @change="gender = $event"
                            :items="genderOptions"
                            :value="gender"
                            outlined dense no-object
                        />
                    </v-col>
                    <v-col cols="12">
                        <kimsa-text-field
                            label="Email"
                            :rules="allRules.email"
                            @change="email = $event"
                            :value="email"
                            outlined dense
                            inputMode="email"
                            :disabled="!editMode"
                        />
                    </v-col>
                    <v-col cols="12" md="6" v-if="!readonly || !!region">
                        <kimsa-text-field
                            v-if="!editMode"
                            label="Region"
                            :value="regionValue"
                            outlined dense
                            :disabled="!editMode"
                        />
                        <kimsa-combobox
                            v-else
                            label="Region"
                            :rules="allRules.region"
                            @change="region = $event"
                            :items="regionOptions"
                            :value="region"
                            outlined dense
                            force-value
                        />
                    </v-col>
                    <v-col cols="12" md="6" v-if="!readonly || !!city">
                        <kimsa-text-field
                            v-if="!editMode"
                            label="Comuna"
                            :value="city"
                            outlined dense
                            :disabled="!editMode"
                        />
                        <kimsa-combobox
                            v-else
                            label="Comuna"
                            :rules="allRules.city"
                            @change="city = $event"
                            :items="cityOptions"
                            :value="city"
                            outlined dense
                            force-value no-object
                            :disabled="!region"
                        />
                    </v-col>
                    <v-col cols="12" md="6" v-if="!readonly || !!healthForecastName">
                        <kimsa-text-field
                            v-if="!editMode"
                            label="Previsión de salud"
                            :value="healthForecastName"
                            outlined dense
                            :disabled="!editMode"
                        />
                        <kimsa-select
                            v-else
                            label="Previsión de salud"
                            :rules="allRules.healthForecast"
                            @change="healthForecast = $event"
                            :items="healthForecastOptions"
                            :value="healthForecast"
                            outlined dense no-object
                        />
                    </v-col>
                </v-row>
            </v-form>
        </template>
        <!-- ANAMNESIS -->
        <template v-if="currentTab === 'anamnesis'">
            <v-col cols="12">
                <div class="subheader-heavy primary-color pb-5">
                    Antecedentes personales
                </div>
                <kimsa-button-selector
                    :items="personalOptions"
                    @change="personal = $event"
                    :value="personal"
                    item-value="value"
                    no-object force-cols
                    :readonly="!editMode"
                />
            </v-col>
            <v-col cols="12">
                <v-divider class="divider-secondary"/>
            </v-col>
            <v-col cols="12">
                <div class="subheader-heavy primary-color pb-5">
                    Antecedentes familiares
                </div>
                <kimsa-button-selector
                    :items="familiarOptions"
                    @change="familiar = $event"
                    :value="familiar"
                    item-value="value"
                    no-object force-cols
                    :readonly="!editMode"
                />
            </v-col>
        </template>
        <!-- BUTTONS -->
        <v-col cols="12" class="text-center" v-if="!readonly">
            <v-btn class="normal-btn" text color="primary" v-if="editMode" @click="onCancelEdit">
                Cancelar
            </v-btn>
            <v-btn
                class="normal-btn"
                :outlined="!editMode"
                color="primary"
                @click="onEditClick"
                :loading="loadingSave"
                :disabled="disabled"
            >
                {{ editMode ? 'Guardar' : 'Editar Perfil' }}
            </v-btn>
        </v-col>
    </v-row>
</template>

<script>
import KimsaTabs from "@/components/KimsaTabs";
import LabelInput from "@/components/common/LabelInput";
import KimsaButtonSelector from "@/components/common/inputs/KimsaButtonSelector";
import {options} from "@/utils/Options";
import {rules} from "@/utils/Rules";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaSelect from "@/components/common/inputs/KimsaSelect";
import httpService from "@/services/httpService";

export default {
    name: "ClientProfileInfo",
    components: {
        KimsaSelect,
        KimsaCombobox,
        KimsaDatePicker,
        KimsaTextField,
        KimsaButtonSelector,
        LabelInput,
        KimsaTabs
    },
    props: {
        user: {
            type: Object,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            tabs: [
                {name: 'Datos personales', value: 'personalInfo'},
                {name: 'Historial médico', value: 'anamnesis'},
            ],
            currentTab: 'personalInfo',
            editMode: false,
            loadingSave: false,
            valid: false,

            name: '',
            lastName: '',
            birthDate: '',
            rut: '',
            phone: '',
            gender: '',
            email: '',
            region: null,
            city: null,
            healthForecast: null,
            personal: [],
            familiar: [],

            genderOptions: [],
            regionOptions: options.regionOptions,
            healthForecastOptions: [],
            personalOptions: [],
            familiarOptions: [],
        }
    },
    computed: {
        disabled(vm = this) {
            if (!vm.editMode) return false
            return !vm.valid
        },
        allRules() {
            return {
                name: [rules.required, rules.minLength(this.name, 3)],
                lastName: [rules.required, rules.minLength(this.name, 3)],
                birthDate: [rules.required],
                gender: [rules.required],
                phone: [rules.required],
                rut: [rules.required, rules.rut],
                email: [rules.required, rules.email],
                region: [],
                city: this.region ? [rules.required] : [],
                healthForecast: [],
            }
        },
        cityOptions(vm = this) {
            if (!vm.region) return []
            return vm.region.cities
        },
        healthForecastName(vm = this) {
            if (vm.healthForecastOptions.length === 0 || !vm.user.health_forecast_id) return ''
            return vm.healthForecastOptions.find(op => op.id === vm.user.health_forecast_id)?.name || ''
        },
        regionValue(vm = this) {
            return vm.region?.name || vm.region || null
        },
        genderValue(vm = this) {
            if(vm.genderOptions?.length === 0) return ''
            return vm.genderOptions.find(gender => vm.gender === gender.id)?.name || ''
        },
    },
    async mounted() {
        const vm = this
        await vm.loadProfileBasicInfo()
        vm.setDefaults()
    },
    methods: {
        async loadProfileBasicInfo() {
            const vm = this
            let response = await httpService.get('profile-basic-info')
            // console.log('response loadProfileBasicInfo', response)
            vm.healthForecastOptions = response.data.health_forecasts
            vm.personalOptions = response.data.personal
            vm.familiarOptions = response.data.familiar
            vm.genderOptions = response.data.genders
        },
        setDefaults() {
            const vm = this
            if(!vm.user) return;
            vm.name = vm.user.name
            vm.lastName = vm.user.last_name
            vm.birthDate = vm.$moment(vm.user?.birth_date).format('YYYY-MM-DD')
            vm.rut = vm.user?.rut && vm.$options.filters.rutificador(vm.user?.rut) || ''
            vm.phone = vm.user.phone
            vm.gender = vm.user.gender_id
            vm.email = vm.user.email
            vm.region = vm.user?.region && vm.regionOptions.find(op => op.name === vm.user.region) || null
            vm.city = vm.user?.region && vm.cityOptions.find(op => op === vm.user.city) || null
            vm.healthForecast = vm.user?.health_forecast_id || null
            vm.familiar = vm.getIdsFromArray(vm.user.records, 'type', 'familiar')
            vm.personal = vm.getIdsFromArray(vm.user.records, 'type', 'personal')
        },
        async onEditClick() {
            const vm = this
            vm.rut = vm.rut.replaceAll('.', '').replace('-', '')
            vm.editMode ? await vm.save() : vm.$refs.form.validate()
            vm.editMode = !vm.editMode
        },
        onCancelEdit() {
            const vm = this
            vm.setDefaults()
            vm.editMode = false
        },
        async save() {
            const vm = this
            vm.loadingSave = true

            let payload = {
                name: vm.name,
                last_name: vm.lastName,
                birth_date: vm.birthDate,
                gender_id: vm.gender,
                phone: vm.phone,
                rut: vm.rut.replaceAll('.', '').replaceAll('-', ''),
                email: vm.email,
                records: [...vm.personal, ...vm.familiar]
            }

            let region = vm.region?.name || vm.region || null
            if (region && vm.city) {
                payload.region = region
                payload.city = vm.city
            }
            if (vm.healthForecast) payload.health_forecast_id = vm.healthForecast
            console.log('payload',payload)
            let response = await httpService.put(`users/${vm.user.id}/edit`, payload)
            vm.toast(response.data.message)
            await vm.$store.dispatch('auth/fetchAuthenticatedUser').catch(err => {
                vm.$store.dispatch('auth/refreshToken')
            })
            vm.setDefaults()
            vm.loadingSave = false
        }
    },
    watch: {
        user() {
            this.setDefaults()
        },
    }
}
</script>

<style scoped>

</style>